import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { constants, verifiers } from '@yola/subscription-manager-js';
import { t } from '../localization';
import helpers from './helpers';
import viewMode from './constants/view-mode';
import planFeaturesGroupIds from './constants/plan-features-gruop-ids';
import yoproductsServiceClient from '../common/helpers/yoproducts-service-client';
import { API_TERM_VALUES, PER_MONTH, PER_YEAR } from '../common/constants/periods';
import usePlanFeatures from './hooks/use-plan-features';
import usePlansData from './hooks/use-plans-data';
import useCompactPlansData from './hooks/use-compact-plans-data';
import useAvailablePlans from './hooks/use-available-plans';
import useMatchBreakpoint from '../common/hooks/use-match-breakpoint';
import breakpoints from '../common/constants/breakpoints';
import planNameWithReinforcement from '../common/constants/plan-name-with-reinforcement';
import generatePaymentUrl from '../utils/generate-payment-url';
import useSortedData from './hooks/use-sorted-data';
import isAnnualAvailable from './verifiers/is-annual-available';
import getPlanNames from './helpers/get-plan-names';
import getPromoCoupon from './helpers/get-promo-coupon';
import DifmBanner from '../difm/offer-banner';
import {
  getDifmBannerSessionStorageValue,
  setDifmBannerSessionStorageValue,
} from './helpers/difm-banner-session-storage-manager';
import GradientIcon from './components/gradient-icon';

const {
  SubscriptionType: { DOMAIN_SUBSCRIPTION, B2C_HOSTING_GOLD },
  SubscriptionTerm: { MONTHLY, ANNUAL },
  hostingPackageFeatureNames: features,
} = constants;

const {
  Paragraph,
  Heading,
  Container,
  Row,
  Col,
  CompactPlans,
  PaywallHeader,
  PaywallBody,
  PaywallFooter,
  ButtonsHeader,
  Box,
  Stack,
  HorizontalStack,
} = designSystem;

const {
  getDomainPromoCaptions,
  getAnnualDiscountCaptions,
  getCollapseCaptions,
  getFooterCaptions,
  getToggleData,
  getHighlightedPlanType,
} = helpers;

const defaultCaptions = {};
const defaultPaymentMethods = [];
const defaultDisabledPlans = [];
const defaultFeatureFLags = {};
const defaultProducts = [];

function MultipurposePaywall({
  captions = defaultCaptions,
  currency = 'USD',
  userId = '',
  domainDiscount = null,
  defaultPaymentPeriod = PER_YEAR,
  onPeriodChange = Function.prototype,
  currentPlan,
  currentTerm = '',
  periodTogglerDisabled = false,
  periodTogglerHidden = false,
  minRequiredPlan = '',
  domainName = '',
  siteId = null,
  onSelect,
  appElementSelector = 'body',
  disabledPlans = defaultDisabledPlans,
  featureFlags = defaultFeatureFLags,
  onTooltipHover = Function.prototype,
  onScroll = Function.prototype,
  view = viewMode.DIALOG,
  onBackButtonClick = null,
  onClose = null,
  initiator = '',
  backUrl = '',
  successUrl = '',
  failUrl = '',
  closeUrl = '',
  moneyBackGuarantee = 0,
  paymentMethods = defaultPaymentMethods,
  partnerId,
  partnerName,
  availablePlans,
  isB2C,
  products = defaultProducts,
  highlightedFeatures = null,
  isCtaVisible = true,
  isTrialDomainDiscountAvailable = false,
  isFreePlanAvailable = false,
  isLimitedOfferAvailable = false,
  isDifmOfferBannerVisible = false,
  isIntroDiscountAvailable = false,
}) {
  const [prices, setPrices] = useState({});
  const [annualAvailable, setAnnualAvailable] = useState(false);
  const [paymentPeriod, setPaymentPeriod] = useState(defaultPaymentPeriod);
  const [isLoading, setIsLoading] = useState(true);
  const difmBannerSessionStorageValue = getDifmBannerSessionStorageValue();
  const isInitiallyVisibleDifmBanner = difmBannerSessionStorageValue !== userId;
  const [isDifmBannerVisibleState, setIsDifmBannerVisibleState] = useState(
    isInitiallyVisibleDifmBanner
  );
  const isLgBreakpoint = useMatchBreakpoint(breakpoints.LG);
  const yoproducts = yoproductsServiceClient.get(isB2C);
  const paymentPeriodName = paymentPeriod === PER_YEAR ? 'annual' : 'monthly';
  const FOOTER_CAPTIONS = getFooterCaptions({ moneyBackGuarantee, paymentMethods, partnerName });
  const COLLAPSE_CAPTIONS = getCollapseCaptions();

  const areEcommercePlans = verifiers.isEcommercePlanType(currentPlan);
  const isCompactLayout = !isLgBreakpoint;
  const withNavigation = Boolean(onClose || onBackButtonClick);

  const isDifmbannerShown = isDifmBannerVisibleState && isDifmOfferBannerVisible;

  const { gold_reinforcement: isGoldReinforcementFeatureFlagEnabled } = featureFlags;

  useEffect(() => {
    const fetchPrices = async () => {
      setIsLoading(true);

      const requests = [];

      const domainQueryParams = { discount: domainDiscount };
      requests.push(yoproducts.requests.partnerProductsTree({ partnerId }, domainQueryParams));

      if (isIntroDiscountAvailable) {
        const introQueryParams = { discount: 'intro_discounts' };
        requests.push(yoproducts.requests.partnerProductsTree({ partnerId }, introQueryParams));
      }

      const results = await Promise.all(requests);

      const mergedData = results.reduce(
        (acc, data) => ({
          ...acc,
          ...data,
        }),
        {}
      );

      setPrices(mergedData);
      setAnnualAvailable(isAnnualAvailable(mergedData));
      setIsLoading(false);
    };

    fetchPrices();
  }, [userId, yoproducts.requests, isB2C, partnerId, domainDiscount, isIntroDiscountAvailable]);

  const modifiedAvailablePlans = useMemo(() => {
    if (!isGoldReinforcementFeatureFlagEnabled) return availablePlans;

    return Object.fromEntries(
      Object.entries(availablePlans).map(([key, plan]) => [
        key,
        {
          ...plan,
          name: planNameWithReinforcement[plan.type] || plan.name,
        },
      ])
    );
  }, [isGoldReinforcementFeatureFlagEnabled, availablePlans]);

  const availablePlansData = useAvailablePlans(modifiedAvailablePlans, currentPlan, disabledPlans);

  const isToggleHidden = periodTogglerHidden || !annualAvailable;

  const toggleCaptions = useMemo(() => {
    if (isToggleHidden || periodTogglerDisabled) {
      return null;
    }

    if (domainDiscount && availablePlansData.features.includes(features.FREE_DOMAIN_PROMO)) {
      return getDomainPromoCaptions({
        currency,
        prices,
        availablePlanIds: availablePlansData.planIds,
        domainName,
        domainDiscount,
        isTrialDomainDiscountAvailable,
      });
    }

    return getAnnualDiscountCaptions(currency, prices, availablePlansData.planIds);
  }, [
    availablePlansData,
    currency,
    prices,
    isToggleHidden,
    periodTogglerDisabled,
    domainName,
    domainDiscount,
    isTrialDomainDiscountAvailable,
  ]);

  const onPlanSelect = useCallback(
    (type) => {
      const term = API_TERM_VALUES[paymentPeriod];

      const paymentParams = {
        products: [...products, type, !!domainName && DOMAIN_SUBSCRIPTION].filter(Boolean),
        term,
        ...(domainName && { domain_name: domainName }),
        ...(siteId && { latitude_site_id: siteId }),
        ...(initiator && { initiator }),
        ...(backUrl && { back_url: backUrl }),
        ...(successUrl && { success_url: successUrl }),
        ...(failUrl && { fail_url: failUrl }),
        ...(closeUrl && { close_url: closeUrl }),
        ...(term === ANNUAL && currentTerm === MONTHLY && { p1y_upgrade: true }),
        ...(isIntroDiscountAvailable && { promo: getPromoCoupon(type, term) }),
      };

      const paymentUrl = generatePaymentUrl(paymentParams, isB2C);

      onSelect(paymentUrl, type, paymentPeriodName, paymentParams);
    },
    [
      paymentPeriod,
      products,
      domainName,
      siteId,
      initiator,
      backUrl,
      successUrl,
      failUrl,
      closeUrl,
      currentTerm,
      isIntroDiscountAvailable,
      isB2C,
      onSelect,
      paymentPeriodName,
    ]
  );

  const computedPlans = usePlansData({
    currentPlan,
    currentTerm,
    prices,
    currency,
    minRequiredPlan,
    paymentPeriod,
    availablePlansData,
    areEcommercePlans,
    isTrialDomainDiscountAvailable,
    isFreePlanAvailable,
    isLimitedOfferAvailable,
    isIntroDiscountAvailable,
    captions,
    onPlanSelect,
    isGoldReinforcementFeatureFlagEnabled,
  });

  const handlePeriodChange = (value) => {
    setPaymentPeriod(value);
    onPeriodChange(currentPlan, value === PER_YEAR ? 'annual' : 'monthly');
  };

  const handleTooltipHover = (id) => {
    onTooltipHover(id, currentPlan, paymentPeriodName);
  };

  const handleScroll = (event) => {
    onScroll(event, currentPlan, paymentPeriodName);
  };

  const handleBackButton = () => {
    if (onBackButtonClick) {
      onBackButtonClick();
    } else {
      onClose();
    }
  };

  const handleCloseDifmBanner = useCallback(() => {
    setDifmBannerSessionStorageValue(userId);

    setIsDifmBannerVisibleState(false);
  }, [userId]);

  const handleSubmitDifmBanner = useCallback(() => {
    const term = API_TERM_VALUES[paymentPeriod];
    const paymentParams = {
      products: [B2C_HOSTING_GOLD],
      term: term || MONTHLY,
      ...(siteId && { latitude_site_id: siteId }),
    };
    const paymentUrl = generatePaymentUrl(paymentParams);

    setIsDifmBannerVisibleState(false);
    onSelect(paymentUrl, B2C_HOSTING_GOLD, paymentPeriodName, paymentParams);
  }, [onSelect, paymentPeriod, paymentPeriodName, siteId]);

  const toggleData = getToggleData(paymentPeriod, handlePeriodChange, periodTogglerDisabled);
  const highlightedPlan = getHighlightedPlanType(computedPlans);
  const planFeatures = usePlanFeatures({
    paymentPeriod,
    availablePlansData,
    areEcommercePlans,
    featureFlags,
    isB2C,
    highlightedFeatures,
    domainDiscount,
    isTrialDomainDiscountAvailable,
  });

  const [activeCompactPlans, setCompactPlans] = useState(() => {
    const activePlan = computedPlans.find((plan) => plan.highlighted);
    const activePlans = [];

    if (activePlan) {
      activePlans.push(activePlan.type);
    }

    return activePlans;
  });

  const onCollapseClick = useCallback(
    (type) => {
      const compactPlans = [...activeCompactPlans];
      const index = compactPlans.indexOf(type);

      if (index !== -1) {
        compactPlans.splice(index, 1);
      } else {
        compactPlans.push(type);
      }

      setCompactPlans(compactPlans);
    },
    [activeCompactPlans]
  );

  const compactPlans = useCompactPlansData({
    computedPlans,
    planFeatures,
    activeCompactPlans,
    captions,
    isFreePlanAvailable,
    onCollapseClick,
  });

  const sortedData = useSortedData({
    computedPlans,
    compactPlans,
    planFeatures,
    areEcommercePlans,
    isB2C,
  });

  const getPaywallBody = () => {
    if (areEcommercePlans) {
      return (
        <PaywallBody
          data={sortedData.planFeatures}
          highlightedElementId={highlightedPlan}
          onTooltipHover={handleTooltipHover}
          isLoading={isLoading}
        />
      );
    }

    const planNames = getPlanNames(availablePlansData.plans);

    return (
      <>
        {sortedData.planFeatures.map(({ id, title, data }) =>
          id === planFeaturesGroupIds.CORE ? (
            <PaywallBody
              key={id}
              data={data}
              highlightedElementId={highlightedPlan}
              onTooltipHover={handleTooltipHover}
              isLoading={isLoading}
            />
          ) : (
            !!data.length && (
              <Box paddingTop="spacing-xl" key={id}>
                <PaywallBody
                  groupTitle={title}
                  availablePlans={planNames}
                  data={data}
                  highlightedElementId={highlightedPlan}
                  onTooltipHover={handleTooltipHover}
                  isLoading={isLoading}
                />
              </Box>
            )
          )
        )}
      </>
    );
  };

  const content = (
    <React.Fragment>
      {withNavigation && (
        <ButtonsHeader
          isSticky={isCompactLayout}
          prevButton={{
            onClick: handleBackButton,
            iconGlyph: 'small-left-arrow',
            label: captions.backButtonLabel || t('Back to editor'),
          }}
        />
      )}
      <Container className="g-0">
        <Row className="justify-content-center g-0">
          <Col xs={11} sm={10} md={8} lg={12}>
            {isIntroDiscountAvailable && isLgBreakpoint && (
              <Box paddingTop="spacing-l">
                <HorizontalStack gap="spacing-3-xs" align="center">
                  <GradientIcon />
                  <Heading appearance="gradient" type="heading-6" weight="bold" isUppercase>
                    {/* // It was decided not to localize this string for now  */}
                    INTRO PRICE — Get 70% off select Premium plans
                  </Heading>
                </HorizontalStack>
              </Box>
            )}
            <Box
              paddingBottom="spacing-m"
              {...(withNavigation && {
                paddingTop: 'spacing-xs',
                breakpoints: {
                  md: { paddingTop: 'spacing-2-xl', paddingBottom: 'spacing-0' },
                  lg: {
                    paddingTop: isIntroDiscountAvailable ? 'spacing-3-xs' : 'spacing-2-xl',
                    paddingBottom: 'spacing-m',
                  },
                },
              })}
            >
              <Stack gap="spacing-3-xs">
                <Heading type="heading-1" align="center">
                  <span
                    // eslint-disable-next-line yola/react/no-danger
                    dangerouslySetInnerHTML={{
                      __html:
                        captions.title ||
                        (areEcommercePlans
                          ? t('Upgrade your Online store')
                          : t('Upgrade your subscription')),
                    }}
                  />
                </Heading>

                {captions.description && (
                  <Paragraph align="center">
                    {captions.description.first}
                    <br />
                    {captions.description.second}
                  </Paragraph>
                )}
              </Stack>
            </Box>
            <Box breakpoints={{ md: { marginTop: 'spacing-m' } }}>
              <PaywallHeader
                isLoading={isLoading}
                withIntroPriceDiscounts={isIntroDiscountAvailable}
                isCompact={isCompactLayout}
                plans={sortedData.computedPlans}
                isSticky
                toggle={toggleData}
                isHiddenToggle={isToggleHidden}
                cta={toggleCaptions}
                isCtaVisible={!toggleData.disabled && isCtaVisible}
              />
              {isCompactLayout ? (
                <CompactPlans
                  isLoading={isLoading}
                  withIntroPriceDiscounts={isIntroDiscountAvailable}
                  plans={sortedData.compactPlans}
                  collapseOpenText={COLLAPSE_CAPTIONS.collapseOpenText}
                  collapseHiddenText={COLLAPSE_CAPTIONS.collapseHiddenText}
                />
              ) : (
                getPaywallBody()
              )}
              <PaywallFooter {...FOOTER_CAPTIONS} paymentMethods={paymentMethods} />
            </Box>
          </Col>
        </Row>
      </Container>
      {isDifmbannerShown && (
        <div className="ws-paywall-dialog__offer-banner">
          <div className="ws-paywall-dialog__offer-banner-content">
            <DifmBanner
              partnerName={partnerName}
              planName={
                isGoldReinforcementFeatureFlagEnabled
                  ? planNameWithReinforcement[B2C_HOSTING_GOLD]
                  : B2C_HOSTING_GOLD
              }
              onClose={handleCloseDifmBanner}
              onUpgrade={handleSubmitDifmBanner}
            />
          </div>
        </div>
      )}
    </React.Fragment>
  );

  if (view === viewMode.CONTENT) {
    return content;
  }

  // Hide other page content from screen readers
  ReactModal.setAppElement(appElementSelector);

  return (
    <ReactModal
      isOpen
      className="ws-paywall-dialog"
      overlayClassName={{
        base: 'wp-dialog-overlay wp-dialog-overlay--without-text-styles',
        afterOpen: 'wp-dialog-overlay--after-open',
        beforeClose: 'wp-dialog-overlay--before-close',
      }}
    >
      <div
        className="ws-paywall-dialog__container"
        onScroll={!isCompactLayout ? handleScroll : Function.prototype}
      >
        {content}
      </div>
    </ReactModal>
  );
}

MultipurposePaywall.propTypes = {
  captions: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.shape({
      first: PropTypes.string,
      second: PropTypes.string,
    }),
    backButtonLabel: PropTypes.string,
    plan: PropTypes.shape({
      buttonLabel: PropTypes.string,
    }),
  }),
  currentPlan: PropTypes.string.isRequired,
  currentTerm: PropTypes.string,
  currency: PropTypes.string,
  minRequiredPlan: PropTypes.string,
  domainName: PropTypes.string,
  domainDiscount: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  defaultPaymentPeriod: PropTypes.oneOf([PER_MONTH, PER_YEAR]),
  userId: PropTypes.string,
  siteId: PropTypes.string,
  appElementSelector: PropTypes.string,
  periodTogglerHidden: PropTypes.bool,
  periodTogglerDisabled: PropTypes.bool,
  view: PropTypes.oneOf([viewMode.DIALOG, viewMode.CONTENT]),
  disabledPlans: PropTypes.arrayOf(PropTypes.string),
  featureFlags: PropTypes.shape({}),
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onBackButtonClick: PropTypes.func,
  onScroll: PropTypes.func,
  onTooltipHover: PropTypes.func,
  onPeriodChange: PropTypes.func,
  initiator: PropTypes.string,
  backUrl: PropTypes.string,
  successUrl: PropTypes.string,
  failUrl: PropTypes.string,
  closeUrl: PropTypes.string,
  paymentMethods: PropTypes.arrayOf(PropTypes.string),
  moneyBackGuarantee: PropTypes.number,
  availablePlans: PropTypes.shape().isRequired,
  partnerId: PropTypes.string.isRequired,
  partnerName: PropTypes.string.isRequired,
  isB2C: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(PropTypes.string),
  highlightedFeatures: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      position: PropTypes.number,
      highlightDisabledOnly: PropTypes.bool,
    })
  ),
  isCtaVisible: PropTypes.bool,
  isTrialDomainDiscountAvailable: PropTypes.bool,
  isFreePlanAvailable: PropTypes.bool,
  isLimitedOfferAvailable: PropTypes.bool,
  isDifmOfferBannerVisible: PropTypes.bool,
  isIntroDiscountAvailable: PropTypes.bool,
};

export default MultipurposePaywall;
